import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import CloseButton from "react-bootstrap/CloseButton";
import Form from "react-bootstrap/Form";
import { useEffect, useState } from "react";
import { useAppDispatch } from "src/hooks/redux";
import { submitAddendumOnBehalf } from "../../services/AdminDashService";
import {
  ApplicationAddendum,
  ApplicationAddendumAnswerSubmission,
  ApplicationAddendumQuestion,
} from "src/types/LeaseDomainAddendum";

interface SubmitAddendumOnBehalfModalProps {
  show: boolean;
  onHide: () => void;
  leaseId: string;
  applicantId: string;
  applicantName: string;
  // Every NotStarted addendum for this applicant - most applicants have exactly one, but the
  // modal supports more so a future addendum doesn't need its own UI.
  addendums: ApplicationAddendum[];
  onSubmitted: () => void;
}

const isFreeText = (question: ApplicationAddendumQuestion) =>
  question.questionType === "text" || question.questionType === "single-line";
const isSingleChoice = (question: ApplicationAddendumQuestion) =>
  question.questionType === "single-select" ||
  question.questionType === "boolean";
const isMultiSelect = (question: ApplicationAddendumQuestion) =>
  question.questionType === "multi-select";

const SubmitAddendumOnBehalfModal = ({
  show,
  onHide,
  leaseId,
  applicantId,
  applicantName,
  addendums,
  onSubmitted,
}: SubmitAddendumOnBehalfModalProps) => {
  const dispatch = useAppDispatch();
  const [selectedAddendumId, setSelectedAddendumId] = useState<string>(
    addendums[0]?.addendumId ?? ""
  );
  const [textValues, setTextValues] = useState<Record<string, string>>({});
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<
    Record<string, string[]>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset only on the show:false->true transition, not on every `addendums` identity change -
  // Applicants.tsx passes a freshly-filtered array on every render, so keying on it too would
  // wipe in-progress answers on any unrelated parent re-render while the modal is open.
  useEffect(() => {
    if (!show) return;
    setSelectedAddendumId(addendums[0]?.addendumId ?? "");
    setTextValues({});
    setSelectedAnswerIds({});
    setErrorMessage(null);
  }, [show]);

  // Switching addendums starts a fresh form - answers keyed by formQuestionId would otherwise
  // carry over, and a validation message from the previous one would stay on screen.
  const onAddendumChange = (addendumId: string) => {
    setSelectedAddendumId(addendumId);
    setTextValues({});
    setSelectedAnswerIds({});
    setErrorMessage(null);
  };

  const selectedAddendum = addendums.find(
    (addendum) => addendum.addendumId === selectedAddendumId
  );
  const sortedQuestions = [...(selectedAddendum?.formQuestions ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );
  const unsupportedQuestion = sortedQuestions.find(
    (question) =>
      !isFreeText(question) &&
      !isSingleChoice(question) &&
      !isMultiSelect(question)
  );

  const toggleAnswer = (
    questionId: string,
    answerId: string,
    checked: boolean
  ) => {
    setSelectedAnswerIds((prev) => {
      const current = prev[questionId] ?? [];
      return {
        ...prev,
        [questionId]: checked
          ? [...current, answerId]
          : current.filter((id) => id !== answerId),
      };
    });
  };

  const validate = (): string | null => {
    for (const question of sortedQuestions) {
      if (isFreeText(question)) {
        if (
          question.isRequired &&
          !(textValues[question.formQuestionId] ?? "").trim()
        ) {
          return "Please answer all required questions.";
        }
        continue;
      }
      const selected = selectedAnswerIds[question.formQuestionId] ?? [];
      if (question.isRequired && selected.length === 0) {
        return "Please answer all required questions.";
      }
      if (
        question.minimumAnswers &&
        selected.length < question.minimumAnswers
      ) {
        return `Please select at least ${question.minimumAnswers} option(s) for "${question.questionText}".`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!selectedAddendum || unsupportedQuestion) return;
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    // single-select/multi-select post the option's own answerText, not its id - the write
    // endpoint matches on answerText, matching the resident-facing UI's own submission shape.
    const answers: ApplicationAddendumAnswerSubmission[] =
      sortedQuestions.flatMap((question) => {
        if (isFreeText(question)) {
          const value = (textValues[question.formQuestionId] ?? "").trim();
          return value
            ? [{ questionId: question.formQuestionId, answerValue: value }]
            : [];
        }
        const selected = selectedAnswerIds[question.formQuestionId] ?? [];
        return selected.flatMap((answerId) => {
          const option = question.formQuestionAnswers.find(
            (answer) => answer.answerId === answerId
          );
          return option
            ? [
                {
                  questionId: question.formQuestionId,
                  answerValue: option.answerText,
                },
              ]
            : [];
        });
      });

    // Every question above is optional, so validate() passed with nothing filled in - Lease
    // Domain rejects an empty answers array outright, and submitting a wholly empty addendum
    // isn't something an associate means to do anyway.
    if (answers.length === 0) {
      setErrorMessage("Please answer at least one question.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    const success = await dispatch(
      submitAddendumOnBehalf(
        leaseId,
        applicantId,
        selectedAddendum.addendumId,
        answers
      )
    );
    setIsSubmitting(false);

    if (success) {
      onSubmitted();
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      aria-labelledby="submit-addendum-on-behalf-modal"
    >
      <Modal.Header className="header">
        <Modal.Title className="title" id="submit-addendum-on-behalf-modal">
          Answer Addendum on Behalf of {applicantName}
        </Modal.Title>
        <CloseButton onClick={onHide} />
      </Modal.Header>
      <Modal.Body>
        {addendums.length > 1 && (
          <Form.Group className="mb-3">
            <Form.Label>Addendum</Form.Label>
            <Form.Select
              value={selectedAddendumId}
              onChange={(event) => onAddendumChange(event.target.value)}
            >
              {addendums.map((addendum) => (
                <option key={addendum.addendumId} value={addendum.addendumId}>
                  {addendum.formTitle ?? addendum.addendumId}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {selectedAddendum?.formDisclaimer && (
          <p className="text-muted">{selectedAddendum.formDisclaimer}</p>
        )}

        {unsupportedQuestion && (
          <Alert variant="warning">
            This addendum has a question type this form doesn&apos;t support yet
            (&quot;{unsupportedQuestion.questionType}&quot;) - it can&apos;t be
            submitted here.
          </Alert>
        )}

        {sortedQuestions.map((question) => (
          <Form.Group className="mb-3" key={question.formQuestionId}>
            <Form.Label>{question.questionText}</Form.Label>
            {isFreeText(question) ? (
              <Form.Control
                type="text"
                value={textValues[question.formQuestionId] ?? ""}
                onChange={(event) =>
                  setTextValues((prev) => ({
                    ...prev,
                    [question.formQuestionId]: event.target.value,
                  }))
                }
              />
            ) : isSingleChoice(question) ? (
              [...question.formQuestionAnswers]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((answer) => (
                  <Form.Check
                    key={answer.answerId}
                    type="radio"
                    name={`question-${question.formQuestionId}`}
                    id={`question-${question.formQuestionId}-answer-${answer.answerId}`}
                    label={answer.answerText}
                    checked={
                      (selectedAnswerIds[question.formQuestionId] ?? [])[0] ===
                      answer.answerId
                    }
                    onChange={() =>
                      setSelectedAnswerIds((prev) => ({
                        ...prev,
                        [question.formQuestionId]: [answer.answerId],
                      }))
                    }
                  />
                ))
            ) : isMultiSelect(question) ? (
              [...question.formQuestionAnswers]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((answer) => (
                  <Form.Check
                    key={answer.answerId}
                    type="checkbox"
                    id={`question-${question.formQuestionId}-answer-${answer.answerId}`}
                    label={answer.answerText}
                    checked={(
                      selectedAnswerIds[question.formQuestionId] ?? []
                    ).includes(answer.answerId)}
                    onChange={(event) =>
                      toggleAnswer(
                        question.formQuestionId,
                        answer.answerId,
                        event.target.checked
                      )
                    }
                  />
                ))
            ) : (
              <Alert variant="warning">
                Unsupported question type &quot;{question.questionType}&quot;.
              </Alert>
            )}
          </Form.Group>
        ))}

        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedAddendum || !!unsupportedQuestion}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubmitAddendumOnBehalfModal;
